/**
 * The derivation view (CLAUDE.md 5.2, 8.3).
 *
 * This is the product's core promise made visible: "engineers must be able to check
 * the math by hand" (1.1.2). Three things the previous version dropped, each of which
 * broke that promise in a different way:
 *
 * 1. **The substituted line was discarded.** The API sends `subs` -- the exact values
 *    used in the computation -- the report renders them, and the screen showed only
 *    the formula and the answer. So the PDF and the UI disagreed about what the
 *    product's headline feature even is, contradicting the single-renderer principle
 *    of 8.3. Substitution is now rendered here, through the *same formatter* the
 *    report uses (`lib/format.ts` ↔ `calc/core/rounding.py`).
 * 2. **The hierarchy was flattened.** `trace.section(...)` exists precisely so a
 *    ten-span beam stays navigable (5.2); flattening it on the client throws away the
 *    structure the engine went to the trouble of recording.
 * 3. **Verbosity was unreachable.** The API accepts SUMMARY / NORMAL / FULL and the
 *    UI always asked for one of them silently.
 *
 * Symbols, formulas and clause references are never translated (14.4). Section and
 * step titles are i18n keys and always are.
 */
import { useMemo, useState } from 'react'
import * as Collapsible from '@radix-ui/react-collapsible'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { TraceNode } from '@/api/types'
import { cn } from '@/lib/cn'
import { formatSubstitution } from '@/lib/format'
import { ClauseRef } from '@/components/ui/tooltip'

export const VERBOSITY = { SUMMARY: 10, NORMAL: 20, FULL: 30 } as const
export type Verbosity = (typeof VERBOSITY)[keyof typeof VERBOSITY]

export function TraceView({
  trace,
  verbosity = VERBOSITY.NORMAL,
  onVerbosityChange,
}: {
  trace: TraceNode | null
  verbosity?: Verbosity
  onVerbosityChange?: (value: Verbosity) => void
}) {
  const { t } = useTranslation(['common', 'report'])

  const stepCount = useMemo(() => (trace ? countSteps(trace) : 0), [trace])

  if (!trace) return null

  return (
    <section className="space-y-3" aria-label={t('trace.title')}>
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('trace.title')}</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-text-muted">{t('trace.steps', { count: stepCount })}</span>
          {onVerbosityChange && <VerbosityPicker value={verbosity} onChange={onVerbosityChange} />}
        </div>
      </header>

      <ol className="space-y-2">
        <TraceEntries entries={trace.entries ?? []} depth={0} />
      </ol>
    </section>
  )
}

function VerbosityPicker({
  value,
  onChange,
}: {
  value: Verbosity
  onChange: (value: Verbosity) => void
}) {
  const { t } = useTranslation()
  const options: { value: Verbosity; labelKey: string }[] = [
    { value: VERBOSITY.SUMMARY, labelKey: 'trace.verbosity.summary' },
    { value: VERBOSITY.NORMAL, labelKey: 'trace.verbosity.normal' },
    { value: VERBOSITY.FULL, labelKey: 'trace.verbosity.full' },
  ]

  return (
    <div className="flex items-center gap-1.5">
      <label htmlFor="trace-verbosity" className="text-xs text-text-muted">
        {t('trace.verbosity.label')}
      </label>
      <select
        id="trace-verbosity"
        value={value}
        onChange={(event) => onChange(Number(event.target.value) as Verbosity)}
        className="h-9 rounded-md border border-border bg-surface px-2 text-xs text-text"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.labelKey)}
          </option>
        ))}
      </select>
    </div>
  )
}

function TraceEntries({ entries, depth }: { entries: TraceNode[]; depth: number }) {
  return (
    <>
      {entries.map((node, index) =>
        node.kind === 'section' ? (
          <TraceSection key={`${node.title_key}-${index}`} node={node} depth={depth} />
        ) : (
          <TraceStep key={`${node.title_key}-${index}`} node={node} />
        ),
      )}
    </>
  )
}

function TraceSection({ node, depth }: { node: TraceNode; depth: number }) {
  const { t } = useTranslation(['common', 'report'])
  // Top-level sections open, nested ones closed: a long report opens readable rather
  // than as a wall, and an engineer chasing one number is two clicks from it.
  const [open, setOpen] = useState(depth === 0)
  const steps = countSteps(node)

  return (
    <li>
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <Collapsible.Trigger
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-2 text-start',
            'hover:bg-surface-raised',
          )}
        >
          <ChevronRight
            size={15}
            aria-hidden
            className={cn('shrink-0 text-text-muted transition-transform', open && 'rotate-90')}
          />
          <span className="flex-1 text-sm font-medium">{t(node.title_key, { ns: 'report' })}</span>
          {node.ref && <ClauseRef clause={node.ref} />}
          <span className="tabular text-xs text-text-muted">{steps}</span>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <ol className="ms-2 space-y-2 border-s border-border ps-3 pt-1">
            <TraceEntries entries={node.entries ?? []} depth={depth + 1} />
          </ol>
        </Collapsible.Content>
      </Collapsible.Root>
    </li>
  )
}

function TraceStep({ node }: { node: TraceNode }) {
  const { t } = useTranslation(['common', 'report'])
  const subs = node.subs ? Object.entries(node.subs) : []

  return (
    <li className="rounded-md border border-border bg-surface p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{t(node.title_key, { ns: 'report' })}</span>
        {node.ref && <ClauseRef clause={node.ref} />}
      </div>

      {node.formula && (
        // The LaTeX source, shown as-is. The report renders the same string as a
        // build-time SVG (ADR-0009); on screen the monospace source is honest about
        // being the source, and needs no runtime typesetter.
        <code className="mt-1.5 block overflow-x-auto font-mono text-xs text-text-muted">
          {node.formula}
        </code>
      )}

      {subs.length > 0 && (
        // The substituted line: the exact values the calculation used, so the step can
        // be re-keyed by hand. This is the transparency promise (5.2).
        <p className="mt-1.5 flex flex-wrap items-baseline gap-x-1 gap-y-0.5 text-xs">
          {subs.map(([symbol, value], index) => (
            <span key={symbol} className="whitespace-nowrap">
              <span className="italic text-text-muted">{symbol}</span>
              <span className="text-text-muted"> = </span>
              <span className="tabular">{formatSubstitution(value)}</span>
              {index < subs.length - 1 && <span className="text-text-muted"> · </span>}
            </span>
          ))}
        </p>
      )}

      {node.result && (
        <p className="tabular mt-1.5 text-sm font-medium">
          <span className="italic">{node.result.symbol}</span> ={' '}
          {formatSubstitution(node.result.value)} {node.result.unit}
        </p>
      )}

      {node.note_key && (
        <p className="mt-1.5 text-xs text-text-muted">{t(node.note_key, { ns: 'report' })}</p>
      )}
    </li>
  )
}

function countSteps(node: TraceNode): number {
  if (node.kind === 'step') return 1
  return (node.entries ?? []).reduce((total, child) => total + countSteps(child), 0)
}
